import {useTranslation} from "react-i18next";
import {
    Bundle,
    Surcharge,
    useBundleById,
    useFullWastesForBundle,
    useLastTransferForBundle,
    useSurchargesForSubject,
    useUnbundledWastesForJobAtVenue
} from "../rpc/webrpc-client.gen.ts";
import {useIsMyBundle} from "../driver/isMyBundle.ts";
import {getTransferStatus} from "../transit/transitUtils.ts";
import {BundleSurchargeFieldApprovalView} from "./surcharge.tsx";
import {useNavigate} from "@tanstack/react-router";
import {Alert, Box, Modal, Sheet, Stack} from "@mui/joy";
import {useSelf} from "../agent/useSelf.ts";
import {Tile, TileSpread} from "../components/Tile.tsx";
import {SuspenseDialog} from "../components/SuspenseDialog.tsx";
import {useFlavoredBundleStamp} from "./bundle.tsx";
import {useEffect, useMemo, useState} from "react";
import {InspectorWasteTile} from "../inspector/InspectorWasteTile.tsx";
import {SurchargeTile} from "../surcharge/tile.tsx";
import {FieldCancelButton} from "../components/FieldCancelButton.tsx";
import {DriverUnbundledWasteTile, DriverWasteTile} from "../driver/DriverWasteTile.tsx";
import {WasteShortform} from "../waste/shortform.tsx";
import {ActionButton} from "../components/ActionButton.tsx";
import {concat} from "lodash";
import {z} from "zod/v4";
import {useBundleStatus} from "./status.tsx";
import {cleanse, NADA} from "../util/arutil.tsx";
import {useSystemConfig} from "../rpc/sysconf.tsx";
import {SpawnWasteTile} from "../waste/SpawnWasteTile.tsx";

const extraButtonSx = {mx: 2, my: 1, px: 4, py: 2, fontSize: "lg"} as const;

const InspectorWasteTileFood = z.object({
    flavor: z.literal("waste"),
    inspecting: z.literal(true),
    wasteId: z.string(),
});
// type InspectorWasteTileFood = z.infer<typeof InspectorWasteTileFood>;

const DriverWasteTileFood = z.object({
    flavor: z.literal("waste"),
    inspecting: z.literal(false),
    wasteId: z.string(),
});
// type DriverWasteTileFood = z.infer<typeof DriverWasteTileFood>;

const UnbundledWasteTileFood = z.object({
    flavor: z.literal("unbundledWaste"),
    wasteId: z.string(),
    potentialBundleId: z.string(),
});
type UnbundledWasteTileFood = z.infer<typeof UnbundledWasteTileFood>;

const WasteTileFood = z.discriminatedUnion("flavor", [
    InspectorWasteTileFood,
    DriverWasteTileFood,
    UnbundledWasteTileFood,
]);
type WasteTileFood = z.infer<typeof WasteTileFood>;

const SurchargeTileFood = z.object({
    flavor: z.literal("surcharge"),
    surcharge: Surcharge,
});
type SurchargeTileFood = z.infer<typeof SurchargeTileFood>;

const CancelTileFood = z.object({
    flavor: z.literal("cancel"),
    bundle: Bundle,
});
// type CancelTileFood = z.infer<typeof CancelTileFood>;

export const SpawnTileFood = z.object({
    flavor: z.literal("spawn"),
    bundleId: z.string(),
});
export type SpawnTileFood = z.infer<typeof SpawnTileFood>;

export const BundleCargoItemTileFood
    = z.discriminatedUnion("flavor", [
    WasteTileFood,
    SurchargeTileFood,
    CancelTileFood,
    SpawnTileFood,
]);
export type BundleCargoItemTileFood = z.infer<typeof BundleCargoItemTileFood>;

function BundleCargoItemTile(food: BundleCargoItemTileFood) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    switch (food.flavor) {
        case "waste":
            return food.inspecting
                ? <InspectorWasteTile wasteId={food.wasteId} key={food.wasteId}/>
                : <DriverWasteTile wasteId={food.wasteId} key={food.wasteId}/>

        case "unbundledWaste":
            return <DriverUnbundledWasteTile wasteId={food.wasteId} potentialBundleId={food.potentialBundleId}/>

        case "surcharge": {
            const actions = food.surcharge.source === "MANUAL" || food.surcharge.source === "INHERITED"
                ? <ActionButton color="primary" size="lg" perform={() =>
                    navigate({
                        to: "/surcharge/$sc/$id/cargo",
                        params: {sc: food.surcharge.subjectClass, id: food.surcharge.id}
                    })
                }>
                    {t("edit")}
                </ActionButton> : null;
            return <SurchargeTile surcharge={food.surcharge} actions={actions}/>
        }

        case "cancel":
            return <Tile variant="outlined" color="primary">
                <Box sx={{
                    display: "flex",
                    alignItems: "center", justifyContent: "center"
                }}>
                    <FieldCancelButton size="md" bundle={food.bundle} sx={extraButtonSx}/>
                </Box>
            </Tile>

        case "spawn":
            return <SpawnWasteTile bundleId={food.bundleId}/>
    }
}

export function BundleCargoView(args: {
                                    bundleId: string
                                }
) {
    const bundleId = args.bundleId;
    const {data: self} = useSelf();
    const bundleTqrv = useBundleById(args);
    const bundle = bundleTqrv.data;
    const wastesTqrv = useFullWastesForBundle(args);
    const lastTransferTqrv = useLastTransferForBundle(args);
    const lastTransfer = lastTransferTqrv.data;
    const isMyBundleTqrv = useIsMyBundle(args);
    const isMyBundle = isMyBundleTqrv.data;
    const bundleStatusTqrv = useBundleStatus(args);
    const bundleStatus = bundleStatusTqrv.data;
    const lastTransferStatus = getTransferStatus(lastTransfer);
    const iAmAdmin = !!self?.canAdmin;
    const iAmLocalAdmitter = !!self?.admitterFor && self.admitterFor.id === bundle?.destination?.id;
    const iAmLocalInspector = !!self?.inspectorFor && self.inspectorFor.id === bundleStatus?.place?.venue?.id;
    const iAmLocalSupervisor = !!self?.supervisorFor && self.supervisorFor.id === bundleStatus?.place?.venue?.id;
    const useInspectorTile = iAmLocalInspector || iAmLocalSupervisor;
    const jobSurchargesTqrv
        = useSurchargesForSubject(bundle ? {subjectClass: "job", subjectId: bundle.job.id} : undefined);
    const jobSurcharges = jobSurchargesTqrv.data;
    const bundleSurchargesTqrv
        = useSurchargesForSubject(iAmLocalSupervisor ? {subjectClass: "bundle", subjectId: args.bundleId} : undefined);
    const bundleSurcharges = bundleSurchargesTqrv.data;
    const fieldApprovalTqrv
        = useFlavoredBundleStamp({bundleId, flavor: "FIELD_COMPLETE_ACCEPT"});
    const isFieldApproved = fieldApprovalTqrv.isSuccess ? !!fieldApprovalTqrv.data : undefined;
    const fieldProposalTqrv
        = useFlavoredBundleStamp({bundleId, flavor: "FIELD_COMPLETE_PROPOSE"});
    const isFieldProposed = fieldProposalTqrv.isSuccess ? !!fieldProposalTqrv.data : undefined;
    const sysConfTqrv = useSystemConfig();
    // const wasteInspectionStagesTqrv = useQueries(wastesTqrv.data
    //     ? {
    //         queries: wastesTqrv.data.map(waste => wasteStatusTqo(waste
    //             ? {tqc, mama, args: {wasteId: waste.id}} : undefined)),
    //         combine: mergeUseQueriesWithInfill,
    //     }
    //     : {queries: NADA});
    // let watchedWastes: Waste[] = NADA;
    // if (wastesTqrv.data) {
    //     const wasteIndexen = indexesOf(wastesTqrv.data);
    //     const sparseWatchedWastes: (Waste | undefined)[] = wasteIndexen.map(ix => {
    //         if (wasteInspectionStagesTqrv.data[ix]?.inspectionStage
    //             && inspectionStageColorMap?.has(wasteInspectionStagesTqrv.data[ix].inspectionStage)) {
    //             return wastesTqrv.data[ix];
    //         } else {
    //             return undefined;
    //         }
    //     });
    //     watchedWastes = cleanse(sparseWatchedWastes);
    // }

// this is designed to ensure that the shortform only appears once per mount.
// otherwise, it's possible to get into a race situation in which the wastes
// do not post back through the system before we are asked to render again.
    const [isShortformBurned, setIsShortformBurned] = useState<boolean>(false);

    const isSpawnEnabled = sysConfTqrv.data?.driversCanAddWastes
        && (iAmAdmin || useInspectorTile
            || (!!isMyBundle && lastTransferStatus !== "OFFLOADED")
            || iAmLocalAdmitter);
    const isShowingSurchargeForm = isFieldProposed && !isFieldApproved;

    const watchedWastes = cleanse(wastesTqrv.data ?? NADA);
    const nwastes = watchedWastes.length;
    const [isShortformOpen, setIsShortformOpen]
        = useState<boolean>(false);

    const unbundledWasteStubsTqrv = useUnbundledWastesForJobAtVenue(
        bundle?.job.id && bundle.origin.venue?.id
            ? {jobId: bundle.job.id, venueId: bundle.origin.venue.id}
            : undefined)
    const unbundledWasteStubs = unbundledWasteStubsTqrv.data;

    useEffect(() => {
        if (isSpawnEnabled && wastesTqrv.isSuccess && nwastes === 0
            && !!unbundledWasteStubs && unbundledWasteStubs.length === 0
            && !isShortformBurned) {
            setIsShortformOpen(true);
            setIsShortformBurned(true);
        }
    }, [isShortformBurned, isSpawnEnabled, nwastes, unbundledWasteStubs, wastesTqrv.isSuccess]);

// const {data: unbundledWastes} = useQueries({
//     queries: (unbundledWasteStubs ?? []).map(stub => watchWasteByIdTqo(mama, {wasteId: stub.id})),
//     combine: useMappingMergeUseQueryResultCombiner((rpc) => rpc.waste),
// })

    const tileFoods = useMemo(() => {
        const unbundledWasteFoods = (unbundledWasteStubs ?? []).map<UnbundledWasteTileFood>(stub => ({
            flavor: "unbundledWaste", wasteId: stub.id, potentialBundleId: bundleId
        }));
        const bundledWasteFoods = watchedWastes.map<WasteTileFood>(stub => ({
            flavor: "waste", inspecting: useInspectorTile, wasteId: stub.id
        }));
        const rv = concat<BundleCargoItemTileFood>(
            unbundledWasteFoods,
            bundledWasteFoods,
        );

        if (!!self?.supervisorFor || (sysConfTqrv.isSuccess && !sysConfTqrv.data.fieldConfirmSurcharges)) {
            const jobSurchargeFoods: SurchargeTileFood[] = (jobSurcharges ?? []).map(surcharge => ({
                flavor: "surcharge", surcharge
            }));
            rv.push(...jobSurchargeFoods);

            const bundleSurchargeFoods: SurchargeTileFood[] = (bundleSurcharges ?? []).map(surcharge => ({
                flavor: "surcharge", surcharge
            } as const));
            rv.push(...bundleSurchargeFoods);
        }

        if (isSpawnEnabled) {
            rv.push({flavor: "spawn", bundleId});
        }
        if (!!bundle && watchedWastes.length < 1) {
            rv.push({flavor: "cancel", bundle});
        }
        return rv;
    }, [unbundledWasteStubs, watchedWastes, self?.supervisorFor, sysConfTqrv.isSuccess,
        sysConfTqrv.data?.fieldConfirmSurcharges, isSpawnEnabled, bundle, bundleId,
        useInspectorTile, jobSurcharges, bundleSurcharges]);

    if (bundleTqrv.isPending || lastTransferTqrv.isPending || wastesTqrv.isPending ||
        (!!lastTransfer && (fieldProposalTqrv.isPending || fieldApprovalTqrv.isPending))) {
        return <SuspenseDialog message="bundle cargo"/>;
    }

    if (!bundle) {
        return <Alert color="danger" variant="solid">
            no such active bundle
        </Alert>
    }

    return <Stack direction="column" spacing={1.2}>
        <TileSpread foods={tileFoods} Component={({food}) =>
            <BundleCargoItemTile {...food}/>} estimatedHeight={180}/>

        {isShowingSurchargeForm
            ? <BundleSurchargeFieldApprovalView bundleId={bundleId} mode="rw"/>
            : null}

        <Modal open={isShortformOpen}
               onClose={() => {
                   setIsShortformOpen(false);
               }}
               sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
            <Sheet sx={{
                maxWidth: "95%",
                borderRadius: "md",
                p: 3,
                boxShadow: "lg"
            }}>
                <WasteShortform bundleId={bundleId} onClose={() => {
                    setIsShortformOpen(false);
                }}/>
            </Sheet>
        </Modal>
    </Stack>
}
